import React, { useState } from 'react';
import { updateOrderingRules } from '../../services/purchaseOptionService';
import { BTN_PRIMARY, BTN_TERTIARY, ALERT_ERROR } from './orderingStyles';

/**
 * Edit ONE vendor product's ordering rules — the SELECTED purchase option's
 * moq_packages ("Minimum order"), order_increment ("Ordered in") and
 * allows_fractional_packages ("Allow fractional order"). Mounted from Related
 * Products / Vendor Options, the Savings Review flip-card, and the draft PO
 * review; all three edit the SAME purchase_options row through
 * purchaseOptionService.updateOrderingRules, so there is exactly one stored
 * copy of a vendor minimum.
 *
 * The quantity unit is the option's own purchase_unit and is not editable
 * here: for a case product an MOQ of 2 is two CASES, for an each product an
 * MOQ of 6 is six EACH. Typing an unrelated unit for moq_packages is exactly
 * the ambiguity ("2 of whose case?") the purchase-option model exists to
 * prevent.
 *
 * Saving updates purchase_options only. The DB's order-facts token trigger
 * re-prices every open, un-fanned allocation line selecting this option in
 * the same transaction; `onSaved` is where the caller re-reads its own data.
 */
const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 10002, background: 'rgba(43,29,20,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 16, paddingRight: 16, paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' },
  box: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420, boxShadow: '0 8px 40px rgba(43,29,20,0.22)', overflow: 'hidden', fontFamily: 'inherit' },
  header: { padding: '16px 20px 12px', borderBottom: '1px solid #e8e0d5' },
  title: { fontSize: 16, fontWeight: 800, color: '#2b1d14', margin: 0 },
  subtitle: { fontSize: 12.5, color: '#7b6b61', marginTop: 3 },
  body: { padding: '14px 20px' },
  row: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#5A4A42', marginBottom: 5 },
  inputWrap: { display: 'flex', alignItems: 'center', gap: 8 },
  input: { width: 90, boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 14, color: '#2b1d14', background: '#fff', outline: 'none', fontFamily: 'inherit', textAlign: 'right' },
  unit: { fontSize: 13, color: '#2b1d14', fontWeight: 600 },
  helper: { fontSize: 11.5, color: '#7b6b61', marginTop: 4, lineHeight: 1.45 },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: 8 },
  footer: { padding: '12px 20px 16px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #e8e0d5' },
  error: { ...ALERT_ERROR, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12.5 },
};

const positiveOrEmpty = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? String(n) : '';
};

const plural = (count, noun) => {
  const unit = String(noun || 'package').trim() || 'package';
  return Number(count) === 1 || unit.endsWith('s') ? unit : `${unit}s`;
};

export default function OrderingRulesEditor({ option, productLabel, onClose, onSaved }) {
  const [moqDraft, setMoqDraft] = useState(positiveOrEmpty(option?.moq_packages) || '1');
  const [incrementDraft, setIncrementDraft] = useState(positiveOrEmpty(option?.order_increment));
  const [allowsFractional, setAllowsFractional] = useState(option?.allows_fractional_packages === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const unit = String(option?.purchase_unit || 'package').trim() || 'package';
  const moqNum = Number(moqDraft);
  const incrementNum = Number(incrementDraft);
  const moqValid = moqDraft === '' || (Number.isFinite(moqNum) && moqNum > 0);
  const incrementValid = incrementDraft === '' || (Number.isFinite(incrementNum) && incrementNum > 0);
  // A fractional increment on a not-fractional option cannot be honored: the
  // grid would demand part-packages the vendor will not cut.
  const incrementWhole = incrementDraft === '' || allowsFractional
    || Math.abs(incrementNum - Math.round(incrementNum)) < 1e-9;

  const handleSave = async () => {
    if (!moqValid || !incrementValid || !incrementWhole || saving) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateOrderingRules(option.id, {
        moqPackages: moqDraft === '' ? 1 : moqNum,
        orderIncrement: incrementDraft === '' ? null : incrementNum,
        allowsFractional,
      });
      if (onSaved) await onSaved(updated);
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not save the ordering rules.');
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div style={S.box} role="dialog" aria-modal="true" aria-label="Edit ordering rules">
        <div style={S.header}>
          <div style={S.title}>Ordering rules</div>
          <div style={S.subtitle} data-testid="ordering-rules-product">
            {productLabel || [option?.vendor_description, option?.pack_description].filter(Boolean).join(' · ') || 'Vendor product'}
            {' — sold by the '}{unit}
          </div>
        </div>
        <div style={S.body}>
          {error && <div style={S.error} data-testid="ordering-rules-error">{error}</div>}
          <div style={S.row}>
            <label style={S.label} htmlFor="orderingRulesMoq">Minimum order</label>
            <div style={S.inputWrap}>
              <input
                id="orderingRulesMoq"
                data-testid="ordering-rules-moq"
                type="number" min="0" step="any" inputMode="decimal"
                value={moqDraft}
                onChange={(e) => setMoqDraft(e.target.value)}
                style={{ ...S.input, borderColor: moqValid ? '#d5c9c0' : '#ef9a9a' }}
              />
              <span style={S.unit}>{plural(moqNum || 2, unit)}</span>
            </div>
            <div style={S.helper}>
              The least this vendor sells per order line. Blank saves the
              default minimum of 1 {unit}.
            </div>
          </div>
          <div style={S.row}>
            <label style={S.label} htmlFor="orderingRulesIncrement">Ordered in</label>
            <div style={S.inputWrap}>
              <input
                id="orderingRulesIncrement"
                data-testid="ordering-rules-increment"
                type="number" min="0" step="any" inputMode="decimal"
                value={incrementDraft}
                onChange={(e) => setIncrementDraft(e.target.value)}
                style={{ ...S.input, borderColor: incrementValid && incrementWhole ? '#d5c9c0' : '#ef9a9a' }}
              />
              <span style={S.unit}>{plural(incrementNum || 2, unit)} steps</span>
            </div>
            <div style={S.helper}>
              {allowsFractional
                ? `Blank lets the vendor cut any exact quantity; a stated step (0.25, 5, …) makes orders land on that grid.`
                : `Blank means whole ${plural(2, unit)}. A step of 5 means 5, 10, 15…`}
            </div>
            {!incrementWhole && (
              <div style={{ ...S.helper, color: '#b71c1c' }} data-testid="ordering-rules-increment-warning">
                A fractional step needs “Allow fractional order” — otherwise the
                vendor only ships whole {plural(2, unit)}.
              </div>
            )}
          </div>
          <div style={S.row}>
            <label style={S.checkboxRow}>
              <input
                type="checkbox"
                data-testid="ordering-rules-fractional"
                checked={allowsFractional}
                onChange={(e) => setAllowsFractional(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#2b1d14' }}>Allow fractional order</span>
                <div style={S.helper}>
                  Cut-to-order products only — the vendor will ship 0.7 of a {unit}.
                </div>
              </span>
            </label>
          </div>
        </div>
        <div style={S.footer}>
          <button type="button" style={{ ...BTN_TERTIARY, fontSize: 13 }} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            data-testid="ordering-rules-save"
            style={{ ...BTN_PRIMARY, fontSize: 13, opacity: moqValid && incrementValid && incrementWhole ? 1 : 0.5 }}
            onClick={handleSave}
            disabled={saving || !moqValid || !incrementValid || !incrementWhole}
          >
            {saving ? 'Saving…' : 'Save ordering rules'}
          </button>
        </div>
      </div>
    </div>
  );
}
